import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <div className="flex flex-col min-h-screen bg-white">
      <SEO
        title="Privacy Policy"
        description="RC Studios privacy policy. Learn how we collect, use, and protect your personal information when you use our photography and media services."
        path="/privacy-policy"
        pageType="Legal"
      />

      {/* Header */}
      <section className="bg-[#050816] text-white pt-20 pb-12">
        <div className="container mx-auto px-6 max-w-4xl">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">Privacy Policy</h1>
          <p className="text-white/60">
            <strong className="text-white">RC Studios</strong> · Kingman, Arizona
          </p>
          <p className="text-white/60 text-sm mt-2">
            <strong>Effective date:</strong> September 29, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-8">
              RC Studios ("we," "us," "our") respects your privacy. This policy explains what information we collect when you contact us through rcstudios.net, our social media pages, or our advertisements (including Facebook and Instagram lead forms), and how we use it.
            </p>

            <h2 className="font-serif text-2xl text-[#050816] mt-10 mb-4">Information we collect</h2>
            <p className="text-gray-700 mb-4">When you request a quote, book a session, or submit a form, we may collect:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li>Your name</li>
              <li>Phone number</li>
              <li>Email address</li>
              <li>Your preferred contact method (text message, phone call, or email) and best time to reach you</li>
              <li>The service you're interested in and any details you choose to share about your project</li>
              <li>Company or brand name (if applicable)</li>
            </ul>
            <p className="text-gray-700 mb-6">
              We may also receive basic technical information (such as browser type and pages visited) through standard website analytics and advertising tools, including Meta (Facebook) tools.
            </p>

            <h2 className="font-serif text-2xl text-[#050816] mt-10 mb-4">How we use your information</h2>
            <p className="text-gray-700 mb-4">We use your information only to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li>Respond to your inquiry and provide quotes</li>
              <li>Schedule and deliver photography and related services</li>
              <li>Send appointment reminders, gallery links, and service-related follow-ups</li>
              <li>Occasionally share offers or updates from RC Studios, which you can opt out of at any time</li>
            </ul>
            <p className="text-gray-700 font-semibold mb-6">
              We do not sell, rent, or trade your personal information.
            </p>

            <h2 className="font-serif text-2xl text-[#050816] mt-10 mb-4">Text messages (SMS) and phone calls</h2>
            <p className="text-gray-700 mb-4">
              If you provide your phone number and agree to be contacted, you consent to receive calls and text messages from RC Studios at that number about your inquiry and our services. Message frequency varies. Message and data rates may apply. Consent is not a condition of purchase.
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Reply <strong>STOP</strong> to any text to opt out.</li>
              <li>Reply <strong>HELP</strong> for help, or contact us at (928) 530-6604.</li>
            </ul>
            <p className="text-gray-700 mb-6">
              Mobile numbers and SMS opt-in consent are never shared with third parties for their marketing purposes.
            </p>

            <h2 className="font-serif text-2xl text-[#050816] mt-10 mb-4">How we share information</h2>
            <p className="text-gray-700 mb-6">
              We share information only with service providers who help us run the business (for example, email, scheduling, payment, and gallery-delivery platforms), and only as needed to serve you, or when required by law.
            </p>

            <h2 className="font-serif text-2xl text-[#050816] mt-10 mb-4">Data retention and security</h2>
            <p className="text-gray-700 mb-6">
              We keep your information only as long as needed to serve you and meet business and legal obligations. We use reasonable safeguards to protect it, though no online system is 100% secure.
            </p>

            <h2 className="font-serif text-2xl text-[#050816] mt-10 mb-4">Your choices</h2>
            <p className="text-gray-700 mb-6">
              You can ask us at any time to access, correct, or delete your information, or to stop contacting you. Just email{' '}
              <a href="mailto:marketing@rcstudios.net" className="text-[#C6A14A] hover:underline">marketing@rcstudios.net</a> or call/text{' '}
              <a href="tel:+19285306604" className="text-[#C6A14A] hover:underline">(928) 530-6604</a>.
            </p>

            <h2 className="font-serif text-2xl text-[#050816] mt-10 mb-4">Children's privacy</h2>
            <p className="text-gray-700 mb-6">
              Our services are not directed to children under 13, and we do not knowingly collect their personal information. Parents or guardians book sessions involving minors.
            </p>

            <h2 className="font-serif text-2xl text-[#050816] mt-10 mb-4">Changes to this policy</h2>
            <p className="text-gray-700 mb-6">
              We may update this policy from time to time. The effective date above shows the latest version.
            </p>

            <h2 className="font-serif text-2xl text-[#050816] mt-10 mb-4">Contact us</h2>
            <div className="bg-[#FAF9F6] rounded-xl p-6 border border-gray-100">
              <p className="text-[#050816] font-semibold mb-2">RC Studios</p>
              <p className="text-gray-600 mb-4">Kingman, Arizona</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="mailto:marketing@rcstudios.net" 
                  className="inline-flex items-center gap-2 text-[#C6A14A] hover:text-[#b5923f] transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  marketing@rcstudios.net
                </a>
                <a 
                  href="tel:+19285306604" 
                  className="inline-flex items-center gap-2 text-[#C6A14A] hover:text-[#b5923f] transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  (928) 530-6604
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
